/**
 * Billing Page
 * Shows current plan, usage stats, and upgrade options
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Zap, Building2, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { DashboardLayout } from '../../components/dashboard/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../api/services';
import type { SubscriptionPlan, UsageStats, BillingPeriod } from '../../types';
import { PricingCard } from '../../components/pricing/PricingCard';

export default function BillingPage() {
  const { user, refreshSubscription } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  useEffect(() => {
    loadData();

    // Check for success/cancel from Stripe
    if (searchParams.get('success') === 'true') {
      toast.success('Subscription updated successfully!');
      refreshSubscription();
    } else if (searchParams.get('canceled') === 'true') {
      toast.error('Checkout was canceled');
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [plansData, usageData] = await Promise.all([
        subscriptionService.getPlans(),
        subscriptionService.getUsageStats(),
      ]);
      setPlans(plansData.sort((a, b) => a.sort_order - b.sort_order));
      setUsage(usageData);
    } catch (error) {
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (plan: SubscriptionPlan) => {
    if (plan.name === 'free') return;
    if (user?.subscription?.is_admin_override) {
      toast.error('Your plan was manually assigned by an admin. Contact support to change it.');
      return;
    }

    setCheckoutLoading(plan.name);
    try {
      const response = await subscriptionService.createCheckoutSession({
        plan_name: plan.name as 'pro' | 'business',
        billing_period: billingPeriod,
      });
      window.location.href = response.checkout_url;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await subscriptionService.createBillingPortal();
      window.location.href = response.portal_url;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  const handleRestorePurchases = async () => {
    try {
      await subscriptionService.restorePurchases();
      toast.success('Purchases restored from Stripe');
      refreshSubscription();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to restore purchases');
    }
  };

  const currentPlan = user?.subscription?.plan;
  const currentPlanName = currentPlan?.name || 'free';
  const isAdminOverride = !!user?.subscription?.is_admin_override;

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)}`;
  };

  if (loading) {
    return (
      <DashboardLayout currentPage="billing">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[--clinqr-primary]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="billing">
      <div className="p-8 max-w-6xl mx-auto">
        <h1 className="heading-1 mb-2">Billing</h1>
        <p className="text-[--clinqr-text-secondary] mb-8">Manage your subscription and view usage</p>

        {/* Current Plan & Usage */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Current Plan */}
          <div className="bg-white rounded-xl border border-[--clinqr-border] p-6 shadow-sm">
            <h2 className="text-lg font-medium text-[--clinqr-text-primary] mb-4">Current Plan</h2>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[--clinqr-primary] rounded-lg flex items-center justify-center">
                {currentPlanName === 'business' ? (
                  <Building2 className="w-6 h-6 text-white" />
                ) : currentPlanName === 'pro' ? (
                  <Zap className="w-6 h-6 text-white" />
                ) : (
                  <CreditCard className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <p className="text-xl font-semibold text-[--clinqr-text-primary]">
                  {currentPlan?.display_name || 'Free'}
                </p>
                <p className="text-sm text-[--clinqr-text-secondary]">
                  {currentPlan?.monthly_price_cents
                    ? `${formatPrice(currentPlan.monthly_price_cents)}/month`
                    : 'Free forever'}
                </p>
              </div>
            </div>

            {currentPlanName !== 'free' && !isAdminOverride && (
              <button
                onClick={handleManageBilling}
                className="flex items-center gap-2 text-[--clinqr-primary] hover:text-[--clinqr-primary-dark] text-sm font-medium"
              >
                Manage billing <ExternalLink className="w-4 h-4" />
              </button>
            )}

            {isAdminOverride && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  This plan was manually assigned by an admin. Contact support to change it.
                </p>
              </div>
            )}

            {user?.subscription?.stripe_customer_id && !user?.subscription?.is_admin_override && (
              <button
                onClick={handleRestorePurchases}
                className="flex items-center gap-2 text-[--clinqr-text-secondary] hover:text-[--clinqr-text-primary] text-sm font-medium mt-2"
              >
                Restore purchases
              </button>
            )}

            {user?.subscription?.cancel_at_period_end && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Subscription canceling</p>
                  <p className="text-xs text-amber-600">
                    Your subscription will end on{' '}
                    {user.subscription.current_period_end
                      ? new Date(user.subscription.current_period_end).toLocaleDateString()
                      : 'period end'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Usage Stats */}
          <div className="bg-white rounded-xl border border-[--clinqr-border] p-6 shadow-sm">
            <h2 className="text-lg font-medium text-[--clinqr-text-primary] mb-4">Usage This Month</h2>

            {/* QR Codes */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[--clinqr-text-secondary]">Active QR Codes</span>
                <span className="font-medium text-[--clinqr-text-primary]">
                  {usage?.qr_code_count || 0} / {usage?.qr_code_limit || 1}
                </span>
              </div>
              <div className="h-2 bg-[--clinqr-bg-subtle] rounded-full overflow-hidden border border-[--clinqr-border]/10">
                <div
                  className="h-full bg-[--clinqr-primary] rounded-full transition-all"
                  style={{ width: `${Math.min(usage?.qr_percentage || 0, 100)}%` }}
                />
              </div>
            </div>

            {/* Scans */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[--clinqr-text-secondary]">Scans</span>
                <span className="font-medium text-[--clinqr-text-primary]">
                  {usage?.scan_count?.toLocaleString() || 0}
                  {usage?.scan_limit ? ` / ${usage.scan_limit.toLocaleString()}` : ' / Unlimited'}
                </span>
              </div>
              {usage?.scan_limit && (
                <div className="h-2 bg-[--clinqr-bg-subtle] rounded-full overflow-hidden border border-[--clinqr-border]/10">
                  <div
                    className="h-full bg-[--clinqr-primary] rounded-full transition-all"
                    style={{ width: `${Math.min(usage?.scan_percentage || 0, 100)}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-[--clinqr-bg-subtle] p-1 rounded-lg inline-flex">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-white text-[--clinqr-text-primary] shadow-sm'
                  : 'text-[--clinqr-text-secondary] hover:text-[--clinqr-text-primary]'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                billingPeriod === 'annual'
                  ? 'bg-white text-[--clinqr-text-primary] shadow-sm'
                  : 'text-[--clinqr-text-secondary] hover:text-[--clinqr-text-primary]'
              }`}
            >
              Annual <span className="text-[--clinqr-primary] ml-1">Save 20%</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              billingPeriod={billingPeriod}
              isCurrentPlan={plan.name === currentPlanName}
              onAction={handleAction}
              loading={checkoutLoading === plan.name}
              currentPlanName={currentPlanName}
              plans={plans}
              isAdminOverride={isAdminOverride}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
